/**
 * Утилиты для работы с датами
 */

/**
 * Форматирует дату в формат DD.MM.YYYY
 * 
 * @param date - Дата для форматирования
 * @returns Отформатированная строка даты
 * 
 * @example
 * formatDate(new Date('2025-10-30')) // '30.10.2025'
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) {
    return 'Неверная дата';
  }
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  return `${day}.${month}.${year}`;
}

/**
 * Вычисляет количество дней между двумя датами
 * 
 * @param from - Начальная дата
 * @param to - Конечная дата
 * @returns Количество дней (может быть отрицательным, если to < from)
 * 
 * @example
 * getDaysBetween(new Date('2025-10-30'), new Date('2025-11-06')) // 7
 */
export function getDaysBetween(from: Date | string, to: Date | string): number {
  const fromDate = typeof from === 'string' ? new Date(from) : from;
  const toDate = typeof to === 'string' ? new Date(to) : to;
  
  const diffTime = toDate.getTime() - fromDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * Вычисляет количество дней от текущей даты до указанной
 * 
 * @param date - Целевая дата
 * @returns Количество дней (может быть отрицательным, если дата в прошлом)
 * 
 * @example
 * getDaysRemaining(new Date('2025-11-06')) // количество дней до 6 ноября
 */
export function getDaysRemaining(date: Date | string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Сбрасываем время для точного подсчета дней
  
  return getDaysBetween(now, date);
}

/**
 * Проверяет, истекла ли дата
 * 
 * @param date - Дата для проверки
 * @returns true, если дата в прошлом
 * 
 * @example
 * isDateExpired(new Date('2025-10-01')) // true (если сегодня позже)
 */
export function isDateExpired(date: Date | string): boolean {
  return getDaysRemaining(date) < 0;
}

/**
 * Проверяет, скоро ли истечет дата (в пределах указанного количества дней)
 * 
 * @param date - Дата для проверки
 * @param daysThreshold - Порог в днях (по умолчанию 7)
 * @returns true, если до даты осталось меньше или равно daysThreshold дней
 * 
 * @example
 * isDateExpiringSoon(new Date('2025-11-05'), 7) // true (если до даты меньше 7 дней)
 */
export function isDateExpiringSoon(date: Date | string, daysThreshold = 7): boolean {
  const daysRemaining = getDaysRemaining(date);
  return daysRemaining >= 0 && daysRemaining <= daysThreshold;
}
